import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, QrCode, RefreshCw, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";
import jsQR from "jsqr";
import api from "../../lib/api";
import { usePos } from "../../context/PosContext";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const ACCEPTED_TYPES = {
  "image/png": "PNG",
  "image/jpeg": "JPEG",
  "image/webp": "WEBP",
};

function getApiErrorMessage(err, fallback) {
  return (
    err?.response?.data?.detail?.message ||
    err?.response?.data?.detail ||
    fallback
  );
}

export default function OperationalQRISPage() {
  const { outlet, outletId } = usePos();

  const [data, setData] = useState({ active: null, history: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [qrPayload, setQrPayload] = useState("");
  const [decodeStatus, setDecodeStatus] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  const fileInputRef = useRef(null);

  const loadQRIS = useCallback(async () => {
    if (!outletId) {
      setData({ active: null, history: [] });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await api.get("/operational-qris", {
        params: { outlet_id: outletId },
      });

      setData({
        active: response.data?.active || null,
        history: Array.isArray(response.data?.history)
          ? response.data.history
          : [],
      });
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        "Failed to load operational QRIS."
      );
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    loadQRIS();
  }, [loadQRIS]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const resetUpload = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(null);
    setPreviewUrl("");
    setQrPayload("");
    setDecodeStatus("");
    setUploadError("");
    setUploadSuccess("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const decodeQRCode = async (file) => {
    setDecodeStatus("Reading QR code...");

    try {
      const imageUrl = URL.createObjectURL(file);

      const result = await new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => {
          try {
            const maxDimension = 1600;
            const scale = Math.min(
              1,
              maxDimension / Math.max(image.naturalWidth, image.naturalHeight)
            );

            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
            canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

            const context = canvas.getContext("2d", {
              willReadFrequently: true,
            });

            context.drawImage(image, 0, 0, canvas.width, canvas.height);

            const imageData = context.getImageData(
              0,
              0,
              canvas.width,
              canvas.height
            );

            const code = jsQR(
              imageData.data,
              imageData.width,
              imageData.height,
              {
                inversionAttempts: "attemptBoth",
              }
            );

            URL.revokeObjectURL(imageUrl);
            resolve(code?.data || "");
          } catch (err) {
            URL.revokeObjectURL(imageUrl);
            reject(err);
          }
        };

        image.onerror = () => {
          URL.revokeObjectURL(imageUrl);
          reject(new Error("Unable to read image."));
        };

        image.src = imageUrl;
      });

      if (!result) {
        setDecodeStatus("QR code not detected.");
        setQrPayload("");
        return;
      }

      setQrPayload(result);
      setDecodeStatus("QR code detected.");
    } catch {
      setDecodeStatus("Failed to read QR code.");
      setQrPayload("");
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    setUploadError("");
    setUploadSuccess("");

    if (!file) {
      resetUpload();
      return;
    }

    if (!ACCEPTED_TYPES[file.type]) {
      setUploadError("Only PNG, JPEG, or WEBP images are allowed.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError("Maximum file size is 2 MB.");
      event.target.value = "";
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setQrPayload("");
    setDecodeStatus("Preparing QR code...");

    await decodeQRCode(file);
  };

  const handleUpload = async () => {
    if (!outletId) {
      setUploadError("Please select an outlet first.");
      return;
    }

    if (!selectedFile) {
      setUploadError("Please select a QRIS image.");
      return;
    }

    if (!qrPayload) {
      setUploadError(
        "A valid QR code could not be detected in this image."
      );
      return;
    }

    if (
      data.active &&
      !window.confirm(
        "This will replace the active Operational QRIS for this outlet. Continue?"
      )
    ) {
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadSuccess("");

    try {
      const formData = new FormData();
      formData.append("outlet_id", outletId);
      formData.append("qr_payload", qrPayload);
      formData.append("file", selectedFile);

      if (data.active?.id) {
        await api.post(
          `/operational-qris/${data.active.id}/replace`,
          formData
        );
        setUploadSuccess("Operational QRIS replaced successfully.");
        toast.success("Operational QRIS replaced successfully.");
      } else {
        await api.post("/operational-qris", formData);
        setUploadSuccess("Operational QRIS activated successfully.");
        toast.success("Operational QRIS activated successfully.");
      }

      resetUpload();
      await loadQRIS();
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        "Failed to save operational QRIS."
      );
      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Operational QRIS
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure the QRIS used by customers to pay Self Orders at this
            outlet.
          </p>
        </div>

        <button
          type="button"
          onClick={loadQRIS}
          disabled={loading || !outletId}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw
            className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {outlet && (
        <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
          Configuring outlet:{" "}
          <span className="font-medium">{outlet.name}</span>
          {outlet.code && (
            <span className="ml-2 text-muted-foreground">
              ({outlet.code})
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border bg-card">
          <div className="border-b px-5 py-4">
            <h2 className="font-semibold">Active QRIS</h2>
            <p className="text-sm text-muted-foreground">
              This QRIS belongs to the selected tenant outlet.
            </p>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Loading QRIS...
              </div>
            ) : data.active ? (
              <div className="space-y-5">
                <div className="flex justify-center rounded-xl border bg-white p-4">
                  <img
                    src={`/api/operational-qris/${data.active.id}/image`}
                    alt="Operational QRIS"
                    className="max-h-80 w-auto object-contain"
                  />
                </div>

                <div className="grid gap-3 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Merchant</span>
                    <span className="font-medium text-right">
                      {data.active.merchant_name || "—"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">City</span>
                    <span className="font-medium text-right">
                      {data.active.merchant_city || "—"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Currency</span>
                    <span className="font-medium text-right">
                      {data.active.currency || "IDR"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Status</span>
                    <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Active
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-10 text-center">
                <QrCode className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-3 font-medium">No Operational QRIS</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Upload a QRIS image for this outlet.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border bg-card">
          <div className="border-b px-5 py-4">
            <h2 className="font-semibold">
              {data.active ? "Replace QRIS" : "Upload QRIS"}
            </h2>
            <p className="text-sm text-muted-foreground">
              Upload the QRIS image used by this outlet for Self Order
              payments.
            </p>
          </div>

          <div className="space-y-5 p-5">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileChange}
              className="block w-full text-sm"
              disabled={uploading}
            />

            {previewUrl && (
              <div className="rounded-xl border bg-white p-4">
                <img
                  src={previewUrl}
                  alt="QRIS preview"
                  className="mx-auto max-h-72 w-auto object-contain"
                />
              </div>
            )}

            {selectedFile && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">File</span>
                  <span className="truncate font-medium">
                    {selectedFile.name}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">
                    {ACCEPTED_TYPES[selectedFile.type]}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Size</span>
                  <span className="font-medium">
                    {(selectedFile.size / 1024).toFixed(0)} KB
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">QR Detection</span>
                  <span
                    className={
                      qrPayload
                        ? "font-medium text-emerald-600"
                        : "font-medium text-amber-600"
                    }
                  >
                    {decodeStatus}
                  </span>
                </div>
              </div>
            )}

            {uploadError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{uploadError}</span>
              </div>
            )}

            {uploadSuccess && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-600">
                {uploadSuccess}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              {selectedFile && (
                <button
                  type="button"
                  onClick={resetUpload}
                  disabled={uploading}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  Clear
                </button>
              )}

              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading || !selectedFile || !qrPayload || !outletId}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                {uploading
                  ? "Saving..."
                  : data.active
                    ? "Replace QRIS"
                    : "Activate QRIS"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {data.history?.length > 0 && (
        <div className="rounded-xl border bg-card">
          <div className="border-b px-5 py-4">
            <h2 className="font-semibold">QRIS History</h2>
            <p className="text-sm text-muted-foreground">
              Previous Operational QRIS records for this outlet.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left">
                  <th className="px-5 py-3 font-medium">Merchant</th>
                  <th className="px-5 py-3 font-medium">City</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Activated</th>
                </tr>
              </thead>

              <tbody>
                {data.history.map((item) => (
                  <tr key={item.id} className="border-b last:border-0">
                    <td className="px-5 py-4">
                      {item.merchant_name || "—"}
                    </td>
                    <td className="px-5 py-4">
                      {item.merchant_city || "—"}
                    </td>
                    <td className="px-5 py-4">
                      {item.active ? (
                        <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                          Replaced
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleString("en-ID")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
